/**
 * Single-choice problem input.
 */
import React, { useEffect, useState } from "react";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

import CorrectAnswerCard from "./CorrectAnswerCard";
import WrongAnswerCard from "./WrongAnswerCard";
import { VText } from "form";
import RenderResponsePrompt from "./RenderResponsePrompt";
import SafeHtml from "pages/common/SafeHtml";

/**
 * Radio group built from `<choicegroup>` options.
 *
 * @param {{ tag: Element, onSetAnswer?: Function }} props
 */
export default function MultipleChoiceResponseRender(props) {
  const { tag, grade, onSetAnswer, isShowMark, setShowMark } = props;

  const [options, setOptions] = useState([]);
  const [selection, setSelection] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    if (!tag) return;

    const choicesWrap = tag.querySelector("choicegroup");
    if (!choicesWrap) return;

    const choices = choicesWrap.children;
    const chos = [];

    for (let i = 0; i < choices.length; i++) {
      choices[i].querySelector("choicehint")?.remove();
      chos.push({ value: "choice_" + i, text: choices[i].innerHTML });
      if (choices[i].getAttribute("correct") === "true") setCorrectAnswer("choice_" + i);
    }

    setOptions(chos);
  }, [tag]);

  /**
   * Grades the selected choice and reports the answer.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Change event.
   * @returns {void}
   */
  const onChange = (e) => {
    setShowMark(false);
    setSelection(e.target.value);

    const correct = e.target.value === correctAnswer;

    onSetAnswer({
      id: grade?.id || "",
      student_answer: e.target.value,
      correctness: correct ? "correct" : "incorrect",
    });

    setIsCorrect(correct);
  };

  return (
    <div className="mb-2">
      <RenderResponsePrompt tag={tag} />

      <RadioGroup value={selection} onChange={onChange} className="gap-3">
        {options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={<Radio />}
            label={<VText><SafeHtml as="span" html={opt.text} course /></VText>}
            className={`transition-all delay-50 mr-0 p-2 rounded-md border border-solid hover:bg-gray-50 hover:border-red-500 ${
              selection === opt.value ? "border-gray-100" : "border-gray-300 dark:border-gray-500"
            }`}
          />
        ))}
      </RadioGroup>

      {isShowMark && (isCorrect ? <CorrectAnswerCard /> : <WrongAnswerCard />)}
    </div>
  );
}
